<template>
   <!--Page alert + loader (no table here, so the loader follows "loading")-->
   <PageStatus :alert="defaultAlert" :table-data="{ isVisible: loading, loaderMessage: 'Loading...', loaderValue: null }" />

   <!--Payment Modal-->
   <div id="recordFormModal" class="modal fade" tabindex="-1" data-bs-focus="false" data-bs-backdrop="static">
      <div class="modal-dialog">
         <div class="modal-content bg-sa-modal-content">
            <div class="modal-header bg-sa-modal-header">
               <h5 class="modal-title">
                  {{ actionLabel(selectedPackage) }}: {{ selectedPackage ? selectedPackage.subscription_package : '' }}
               </h5>
               <button type="button" class="btn-close color-sa-btn-close" data-bs-dismiss="modal" :disabled="isPaying"></button>
            </div>

            <div class="modal-body">
               <AlertComponent v-if="defaultAlert.pageOrModal == 'modal'" :alertstatus="defaultAlert.alertstatus" :message="defaultAlert.alertMessage" :type="defaultAlert.alertType" />

               <!-- Number of users -->
               <label for="planUsers" class="form-label">Number of Users</label>
               <input id="planUsers" v-model.number="form.users" type="number" min="1" step="1" inputmode="numeric" class="form-control mb-1" :disabled="isPaying" @change="onUsersChange()">
               <small class="text-muted d-block mb-3 sa-core-color">
                  {{ currency }} {{ formatMoney(packagePrice) }} per user per month.
                  <template v-if="plan.users_in_use"> You have {{ plan.users_in_use }} active user(s).</template>
               </small>

               <!-- Billing cycle -->
               <label for="billingCycle" class="form-label">Billing Cycle</label>
               <select id="billingCycle" v-model.number="form.period" @change="loadQuote()" class="form-select" :disabled="isPaying">
                  <option :value="0" disabled>Select billing cycle...</option>
                  <option v-for="p in periodOptions" :key="p.value" :value="p.value">
                     {{ p.label }} — {{ currency }} {{ formatMoney(packagePrice * userCount * p.value) }}
                  </option>
               </select>

               <!-- Quote -->
               <div v-if="quoteLoading" class="text-muted small mt-3">
                  <img src="/img/progress.gif" height="12" class="me-1" alt=""> Working out your price...
               </div>

               <div v-else-if="quote" class="card mt-3">
                  <div class="card-body p-2 small">
                     <div class="d-flex justify-content-between align-items-center mb-2">
                        <span class="fw-semibold">{{ quote.package_name }} · {{ quote.period_label }}</span>
                        <span :class="typeBadge(quote.type)">{{ typeLabel(quote.type) }}</span>
                     </div>

                     <div class="d-flex justify-content-between">
                        <span>Price ({{ quote.users }} user{{ quote.users === 1 ? '' : 's' }} × {{ quote.period }} month{{ quote.period === 1 ? '' : 's' }})</span>
                        <span>{{ currency }} {{ formatMoney(quote.plan_total) }}</span>
                     </div>
                     <div v-if="quote.credit > 0" class="d-flex justify-content-between text-success">
                        <span>Credit for unused days on {{ quote.current_package_name }}</span>
                        <span>− {{ currency }} {{ formatMoney(Math.min(quote.credit, quote.plan_total)) }}</span>
                     </div>
                     <div v-if="quote.extra_days > 0" class="text-success">
                        Leftover credit adds {{ quote.extra_days }} extra day(s).
                     </div>

                     <hr class="my-2">

                     <div class="d-flex justify-content-between fw-bold fs-6">
                        <span>You pay</span>
                        <span>{{ currency }} {{ formatMoney(quote.amount_due) }}</span>
                     </div>
                     <div class="d-flex justify-content-between text-muted">
                        <span>Plan runs</span>
                        <span>{{ formatDate(quote.start) }} → {{ formatDate(quote.end) }}</span>
                     </div>

                     <div class="mt-2">{{ quote.message }}</div>
                  </div>
               </div>

               <!-- Payment -->
               <template v-if="quote && quote.amount_due > 0">
                  <label for="paymentMethod" class="form-label mt-3">Payment Method</label>
                  <select id="paymentMethod" v-model="form.method" class="form-select" :disabled="isPaying">
                     <option value="">Select payment method...</option>
                     <option v-if="currency === 'KES'" value="mpesa">M-Pesa</option>
                     <option value="paypal">PayPal</option>
                     <option value="card">Debit / Credit Card</option>
                  </select>

                  <div v-if="form.method === 'mpesa'" class="mt-3">
                     <label for="mpesaPhone" class="form-label">M-Pesa Phone Number</label>
                     <input id="mpesaPhone" v-model.trim="form.phone" type="tel" inputmode="numeric" class="form-control" placeholder="e.g. 0712345678" :disabled="isPaying" @keyup.enter="startMpesaPayment()">
                     <div v-if="mpesaWaiting" class="text-info small mt-2">
                        <img src="/img/progress.gif" height="12" class="me-1" alt="">
                        Check your phone and enter your M-Pesa PIN. Waiting for confirmation...
                     </div>
                  </div>

                  <!-- v-show keeps the containers in the page so PayPal can draw into them -->
                  <div v-show="form.method === 'paypal' || form.method === 'card'" class="mt-3">
                     <div v-show="form.method === 'paypal'" id="paypal-button-container"></div>
                                          <div v-show="form.method === 'card'" id="card-button-container"></div>
                     <small v-if="currency !== 'USD'" class="text-muted d-block mt-1">
                        Charged in US dollars: <strong>USD {{ quote.amount_usd }}</strong>
                     </small>
                  </div>
               </template>
            </div>

            <div class="modal-footer bg-sa-modal-footer">
               <button v-if="quote && quote.amount_due <= 0" type="button" class="btn btn-primary me-auto" :disabled="isPaying" @click="activateWithCredit()">
                  <i class="fa fa-check me-1"></i> Activate
               </button>
               <button v-if="quote && quote.amount_due > 0 && form.method === 'mpesa'" type="button" class="btn btn-primary me-auto" :disabled="isPaying" @click="startMpesaPayment()">
                  <i class="fa fa-mobile me-1"></i> Pay {{ currency }} {{ formatMoney(quote.amount_due) }}
               </button>
               <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="isPaying && !mpesaWaiting">Close</button>
            </div>
         </div>
      </div>
   </div>
   <!--End Payment Modal-->

   <h4 class="sa-core-color fw-semibold mb-2">
      <i :class="[pageIcon, 'me-2 opacity-75']"></i>Subscription
   </h4>
   <div v-if="lockedFeature && plan.active" class="alert alert-warning">
      <i class="fa fa-lock me-1"></i>
      <strong>{{ lockedFeature }}</strong> is not included in your <strong>{{ plan.package_name }}</strong> plan.
      Upgrade to a package that includes it.
   </div>

   <!-- Current plan -->
   <div v-if="planLoaded && !plan.active" class="alert alert-danger">
      <i class="fa fa-times me-1"></i>
            <strong>Subscription is required.</strong>
      <span v-if="lockedFeature"> You need an active plan to use <strong>{{ lockedFeature }}</strong>.</span>
      Please choose a package and subscribe.
   </div>
   <div v-else-if="planLoaded" class="alert alert-info">
      <i class="fa fa-check-circle me-1"></i>
      Your plan: <strong>{{ plan.package_name }}</strong>
      <span v-if="plan.users"> · {{ plan.users }} users ({{ plan.users_in_use }} in use)</span>
      <span v-if="plan.end_date"> · ends {{ formatDate(plan.end_date) }}</span>.
      Choose the same package to renew, or another one to change.
   </div>
   <div v-if="plan.scheduled" class="alert alert-warning">
      <i class="fa fa-calendar me-1"></i>
      <strong>{{ plan.scheduled.package_name }}</strong> is paid and starts on {{ formatDate(plan.scheduled.start_date) }}.
   </div>

   <!-- Packages -->
   <div class="pricing-grid">
      <div v-for="(record, index) in packages" :key="record.id"
           class="pricing-card"
           :class="[`pricing-theme-${index % 4}`, { 'pricing-current': isCurrent(record) }]">

         <!-- Corner ribbon -->
         <div v-if="isCurrent(record)" class="pricing-ribbon"><span>Current</span></div>
         <div v-else-if="record.id === popularPackageId" class="pricing-ribbon"><span>Popular</span></div>

         <div class="pricing-name">{{ record.subscription_package }}</div>

         <div class="pricing-price">
            <template v-if="Number(record.price) === 0">FREE</template>
            <template v-else>
               <span class="pricing-currency">{{ currency }}</span>{{ formatMoney(record.price, currency === 'USD' ? 2 : 0) }}<span class="pricing-per"> / user / mo</span>
            </template>
         </div>

         <ul class="pricing-features">
            <li v-for="(feature, idx) in featureItems(record.features)" :key="idx" :class="{ 'is-missing': !feature.included }">
               <i class="fa" :class="feature.included ? 'fa-check' : 'fa-times'"></i>
               <span>{{ feature.text }}</span>
            </li>
         </ul>

         <div class="pricing-footer">
            <button type="button" class="pricing-button" :disabled="!!plan.scheduled" @click="openPackage(record)">
               {{ actionLabel(record) }} <em>now</em>
            </button>
            <small class="pricing-note">
               {{ Number(record.price) > 0 ? (currency === 'KES' ? 'M-Pesa, PayPal and all major cards' : 'PayPal and all major cards') : 'No payment needed' }}
            </small>
         </div>
      </div>

      <div v-if="!loading && packages.length === 0" class="text-center text-muted">No packages available.</div>
   </div>
</template>

<script>
import axios from "axios";
import AlertComponent from "../../../components/ui/AlertComponent.vue";
import PageStatus from "../../../components/admin/PageStatus.vue";
import mySubscriptionMixin from "./mixins/mySubscriptionMixin.js";

const API_URL = "/subscription/api_subscribe.php";
const MPESA_CHECK_EVERY_MS = 3000;
const MPESA_GIVE_UP_AFTER_MS = 2 * 60 * 1000;

export default {
   name: "MySubscription",
   mixins: [mySubscriptionMixin], // MySubscription's own (no table, so no tableMixin)
   components: { AlertComponent, PageStatus },

   data() {
      return {
         loading: false,
         packages: [],
         planLoaded: false,
         plan: { active: false, package_id: null, package_name: null, package_price: null, end_date: null, scheduled: null },
         selectedPackage: null,
         form: { period: 0, method: "", phone: "", users: 1 },
         quote: null,
         quoteLoading: false,
         isPaying: false,
         mpesaWaiting: false,
         mpesaTimer: null,
         mpesaStartedAt: 0,
         currency: "KES",
         periodOptions: [
            { label: "Monthly", value: 1 },
            { label: "Quarterly", value: 3 },
            { label: "Yearly", value: 12 },
         ],
         defaultAlert: { alertMessage: "", alertstatus: "", alertType: "", pageOrModal: "" },
         pageIcon: "fa fa-credit-card", // same icon as this page in the sidebar
      };
   },

   computed: {
      // Set by the router when a page isn't included in the package
      lockedFeature() {
         return this.$route.query.feature || "";
      },
      // The middle paid package gets the "Popular" ribbon (only when there are 3+ paid packages)
      popularPackageId() {
         const paid = this.packages
            .filter(p => Number(p.price) > 0)
            .sort((a, b) => Number(a.price) - Number(b.price));
         return paid.length >= 3 ? paid[Math.floor(paid.length / 2)].id : null;
      },
      packagePrice() {
         return Number(this.selectedPackage?.price || 0);
      },
      // Number of users as a whole number, at least 1
      userCount() {
         return Math.max(1, parseInt(this.form.users, 10) || 1);
      },
      currentPackage() {
         return this.packages.find(p => Number(p.id) === Number(this.plan.package_id)) || null;
      },
   },

   watch: {
      // Buttons are already loaded in the background; only warn if one can't be used
      "form.method"(method) {
         if (method !== "paypal" && method !== "card") return;

         if (typeof paypal === "undefined") {
            this.showMessage(this.currency === "KES"
               ? "PayPal could not load. Please refresh the page or use M-Pesa."
               : "PayPal could not load. Please refresh the page and try again.");
         } else if (this.paypalUnavailable[method]) {
            const other = this.currency === "KES" ? "M-Pesa" : (method === "card" ? "PayPal" : "a card");
            this.showMessage(`${method === "card" ? "Card payments are" : "PayPal is"} not available right now. Please use ${other}.`);
         }
      },
   },

   async created() {
      // PayPal buttons instance (kept out of data() on purpose, so Vue doesn't wrap it)
      this.paypalButtons = [];
      this.paypalUnavailable = {}; // { paypal: true } when PayPal says a button can't be used 
      this.loading = true;
      await Promise.all([this.fetchPackages(), this.fetchCurrentPlan()]);
      this.loading = false;
      this.$checkIfCanFetchLocation(); // Only applies if location is enabled for the user
   },

   mounted() {
      document.getElementById("recordFormModal")?.addEventListener("hidden.bs.modal", this.onModalHidden);
   },

   beforeUnmount() {
      this.closePaypalButtons();
      this.stopMpesaCheck();
      document.getElementById("recordFormModal")?.removeEventListener("hidden.bs.modal", this.onModalHidden);
   },

   methods: {
      /* ---------- Small helpers ---------- */
      showMessage(message, type = "danger", where = "modal", status = "ending") {
         const text = Array.isArray(message) ? message.join(" ") : message;
         this.$displayAlert(status, text, type, where);
      },
      clearMessage() {
         this.defaultAlert = { alertMessage: "", alertstatus: "", alertType: "", pageOrModal: "" };
      },
      // true when the session has ended and the user is being logged out
      handleAuthError(data) {
         if (["JWT_EXPIRED", "CSRF_INVALID", "AUTH_REQUIRED"].includes(data?.code)) {
            this.$forceLogOut("danger", data.message);
            return true;
         }
         return false;
      },
      networkMessage(error) {
         return error?.message === "Network Error"
            ? "No internet connection. Please check your network."
            : "Something went wrong. Please try again later.";
      },
      formatMoney(value, decimals = 2) {
         const n = Number(value);
         return isNaN(n) ? value : n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      },
      // DB dates are UTC "Y-m-d H:i:s"
      formatDate(utc) {
         if (!utc) return "";
         const d = new Date(String(utc).replace(" ", "T") + "Z");
         return isNaN(d) ? utc : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
      },
      splitFeatures(features) {
         return String(features || "").split(",").map(f => f.trim()).filter(Boolean);
      },
      // A feature starting with "-" is shown as not included (✗), e.g. "-Priority support"
      featureItems(features) {
         return this.splitFeatures(features).map(f =>
            f.startsWith("-") ? { text: f.slice(1).trim(), included: false } : { text: f, included: true }
         );
      },
      isCurrent(record) {
         return this.plan.active && Number(record?.id) === Number(this.plan.package_id);
      },
      actionLabel(record) {
         if (!record) return "Subscribe";
         if (!this.plan.active || !this.currentPackage) return "Subscribe";
         if (this.isCurrent(record)) return "Renew";
         return Number(record.price) > Number(this.currentPackage.price) ? "Upgrade" : "Downgrade";
      },
      typeLabel(type) {
         return { new: "New plan", renew: "Renewal", upgrade: "Upgrade", downgrade: "Downgrade" }[type] || type;
      },
      typeBadge(type) {
         return {
            new: "badge bg-primary",
            renew: "badge bg-success",
            upgrade: "badge bg-info text-dark",
            downgrade: "badge bg-warning text-dark",
         }[type] || "badge bg-secondary";
      },
      formData(action, extra = {}) {
         const data = new FormData();
         data.append("action", action);
         data.append("package_id", this.selectedPackage?.id ?? "");
         data.append("period", this.form.period || "");
         data.append("users", this.userCount);
         Object.entries(extra).forEach(([key, value]) => data.append(key, value));
         return data;
      },

      /* ---------- Loading ---------- */
      async fetchPackages() {
         try {
            const { data } = await axios.get(`${API_URL}?action=fetchRecords`, this.$handleConfig());
            if (data.status === "success") {
               this.packages = data.results.records || [];
               this.currency = data.results.currency || "KES";
            } else if (!this.handleAuthError(data)) {
               this.showMessage(data.message, "danger", "page");
            }
         } catch (error) {
            this.showMessage(this.networkMessage(error), "danger", "page");
         }
      },
      async fetchCurrentPlan() {
         try {
            const { data } = await axios.get(`${API_URL}?action=currentPlan`, this.$handleConfig());
            if (data.status === "success") {
               this.plan = data.results.plan;
               this.planLoaded = true;
            } else if (!this.handleAuthError(data)) {
               this.showMessage(data.message, "danger", "page");
            }
         } catch (error) {
            this.showMessage(this.networkMessage(error), "danger", "page");
         }
      },

      /* ---------- Choosing a package ---------- */
      openPackage(record) {
         this.closePaypalButtons();
         this.stopMpesaCheck();
         this.selectedPackage = record;
         // Start with today's number of users (or the users already active, if more)
         this.form = {
            period: 0,
            method: "",
            phone: this.form.phone,
            users: Math.max(1, Number(this.plan.users) || 0, Number(this.plan.users_in_use) || 0),
         };
         this.quote = null;
         this.isPaying = false;
         this.clearMessage();
         this.$showModal("recordFormModal");
      },
      async loadQuote() {
         this.closePaypalButtons(); // the old buttons belong to the old price
         this.quote = null;
         this.form.method = "";
         this.clearMessage();
         if (!this.selectedPackage || !this.form.period) return;

         this.quoteLoading = true;
         try {
            const params = new URLSearchParams({ action: "getQuote", package_id: this.selectedPackage.id, period: this.form.period, users: this.userCount });
            const { data } = await axios.get(`${API_URL}?${params}`, this.$handleConfig());
            if (data.status === "success") {
               this.quote = data.results.quote;
               // Load PayPal + Card buttons in the background while the user reads the price
               this.$nextTick(() => this.renderPaypalButtons());
            } else if (!this.handleAuthError(data)) {
               this.showMessage(data.message);
            }
         } catch (error) {
            this.showMessage(this.networkMessage(error));
         } finally {
            this.quoteLoading = false;
         }
      },

      // Number of users changed: tidy the number and work out the price again
      onUsersChange() {
         this.form.users = this.userCount;
         if (this.form.period) this.loadQuote();
      },

      /* ---------- After a successful payment ---------- */
      async finishPayment(data) {
         await this.closePaypalButtons();
         const r = data.results || {};
         if (r.roles) localStorage.setItem("roles", JSON.stringify(r.roles));
         if (r.subscription) localStorage.setItem("subscription", JSON.stringify(r.subscription));
         if (r.subscription_permissions) localStorage.setItem("subscription_permissions", JSON.stringify(r.subscription_permissions));
         if (r.permissions) {
            localStorage.setItem("permissions", JSON.stringify(r.permissions));
            this.$store.commit("SET_PERMISSIONS", r.permissions); // sidebar refreshes its menus and locks now
         }

         this.stopMpesaCheck();
         this.isPaying = false;
         this.handleSaveOrEditSuccess({ data }); // closes the window and goes to the dashboard (mixin)
      },

      /* ---------- M-Pesa ---------- */
      async startMpesaPayment() {
         if (this.isPaying) return;

         if (!/^(?:\+?254|0)?[17]\d{8}$/.test(this.form.phone.replace(/\s/g, ""))) {
            this.showMessage("Please enter a valid Safaricom number, e.g. 0712345678.", "danger", "modal", "starting");
            return;
         }

         this.isPaying = true;
         this.showMessage("Sending M-Pesa request...", "info", "modal", "starting");

         try {
            const { data } = await axios.post(API_URL, this.formData("startMpesaPayment", { phone: this.form.phone }), this.$handleConfig());
            if (data.status === "success") {
               this.showMessage(data.message, "info", "modal", "starting");
               this.startMpesaCheck(data.results.CheckoutRequestID);
            } else {
               this.isPaying = false;
               if (!this.handleAuthError(data)) this.showMessage(data.message);
            }
         } catch (error) {
            this.isPaying = false;
            this.showMessage(this.networkMessage(error));
         }
      },
      startMpesaCheck(checkoutRequestId) {
         this.stopMpesaCheck();
         this.mpesaWaiting = true;
         this.mpesaStartedAt = Date.now();
         this.mpesaTimer = setInterval(() => this.checkMpesaPayment(checkoutRequestId), MPESA_CHECK_EVERY_MS);
      },
      stopMpesaCheck() {
         if (this.mpesaTimer) clearInterval(this.mpesaTimer);
         this.mpesaTimer = null;
         this.mpesaWaiting = false;
      },
      async checkMpesaPayment(checkoutRequestId) {
         if (Date.now() - this.mpesaStartedAt > MPESA_GIVE_UP_AFTER_MS) {
            this.stopMpesaCheck();
            this.isPaying = false;
            this.showMessage("We haven't received M-Pesa's confirmation yet. If you paid, your plan will switch on as soon as it arrives. Refresh this page in a few minutes.", "warning");
            return;
         }

         try {
            const { data } = await axios.post(API_URL, this.formData("checkMpesaPayment", { CheckoutRequestID: checkoutRequestId }), this.$handleConfig());

            if (data.status === "success") {
               this.finishPayment(data);
            } else if (data.status === "failed" || data.status === "error") {
               this.stopMpesaCheck();
               this.isPaying = false;
               if (!this.handleAuthError(data)) this.showMessage(data.message);
            }
            // "pending": keep waiting
         } catch (error) {
            // A dropped connection shouldn't stop the check; the timeout above still applies
            console.error("M-Pesa check failed:", error);
         }
      },
      onModalHidden() {
         this.closePaypalButtons();
         if (this.mpesaWaiting) {
            this.stopMpesaCheck();
            this.isPaying = false;
            this.showMessage("If you completed the M-Pesa payment, your plan will switch on shortly. Refresh this page in a minute.", "info", "page");
         }
      },
      // Draws BOTH buttons at once, in parallel, into their (hidden) containers
      async renderPaypalButtons() {
         await this.closePaypalButtons();
         this.paypalUnavailable = {};

         if (!this.quote || this.quote.amount_due <= 0) return;
         if (typeof paypal === "undefined") return; // the watcher shows the message if they pick it

         const targets = [
            // eslint-disable-next-line no-undef
            { method: "paypal", id: "paypal-button-container", funding: paypal.FUNDING.PAYPAL },
            // eslint-disable-next-line no-undef
            { method: "card", id: "card-button-container", funding: paypal.FUNDING.CARD },
         ];

         targets.forEach(({ method, id, funding }) => {
            const container = document.getElementById(id);
            if (!container) return;
            container.innerHTML = "";

            // eslint-disable-next-line no-undef
            const buttons = paypal.Buttons({
               fundingSource: funding,

               // The amount comes from the server's quote; the server checks it again with PayPal
               createOrder: (data, actions) => actions.order.create({
                  purchase_units: [{
                     description: `${this.quote.package_name} (${this.quote.period_label})`,
                     amount: { currency_code: "USD", value: Number(this.quote.amount_usd).toFixed(2) },
                  }],
               }),

               onApprove: (data, actions) => {
                  // Show progress at once, so the user knows it's working
                  this.isPaying = true;
                  this.showMessage("Confirming your payment...", "info", "modal", "starting");

                  return actions.order.capture()
                     .then(() => this.completePaypalPayment(data.orderID, method))
                     .catch(() => {
                        this.isPaying = false;
                        this.showMessage("The payment could not be completed. Please try again.");
                     });
               },

               onCancel: () => this.showMessage("Payment was cancelled.", "warning"),
               onError: (err) => {
                  console.error("PayPal error:", err);
                  this.isPaying = false;
                  this.showMessage("Payment failed. Please try again.");
               },
            });

            if (!buttons.isEligible()) {
               this.paypalUnavailable[method] = true;
               return;
            }

            this.paypalButtons.push(buttons);
            buttons.render(`#${id}`).catch(err => console.error("PayPal render error:", err));
         });
      },
      // Close PayPal's windows properly before removing them, so they don't throw "Target window is closed"
      async closePaypalButtons() {
         const list = this.paypalButtons || [];
         this.paypalButtons = [];
         await Promise.all(list.map(b => Promise.resolve().then(() => b.close()).catch(() => {})));
      },
      async completePaypalPayment(orderID, method) {
         this.isPaying = true;
         this.showMessage("Confirming your payment...", "info", "modal", "starting");

         try {
            const { data } = await axios.post(API_URL, this.formData("completePaypalPayment", { orderID, method }), this.$handleConfig());
            if (data.status === "success") {
               this.finishPayment(data);
            } else {
               this.isPaying = false;
               if (!this.handleAuthError(data)) this.showMessage(data.message);
            }
         } catch (error) {
            this.isPaying = false;
            this.showMessage(this.networkMessage(error) + " If money was taken, please contact support with PayPal order " + orderID + ".");
         }
      },
      
      /* ---------- Nothing to pay ---------- */
      async activateWithCredit() {
         if (this.isPaying) return;
         this.isPaying = true;

         try {
            const { data } = await axios.post(API_URL, this.formData("activateWithCredit"), this.$handleConfig());
            if (data.status === "success") {
               this.finishPayment(data);
            } else {
               this.isPaying = false;
               if (!this.handleAuthError(data)) this.showMessage(data.message);
            }
         } catch (error) {
            this.isPaying = false;
            this.showMessage(this.networkMessage(error));
         }
      },
   },
};
</script>